import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { PhoneInput } from '@travellegacy/nexops-design-system';
import { VerificationModal } from '../components/VerificationModal';
import { paymentSchema } from '@/shared/lib/validations';
import { verificationAPI, paymentAPI } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
import { removePhoneMask } from '@/shared/lib/utils';
import { Loader2, CreditCard, CheckCircle2 } from 'lucide-react';
import {
  PaymentType,
  type PaymentFormData,
  type SendVerificationCodeResponse,
  type CreatePaymentPreferenceResponse,
} from '@/shared/types';

export default function Payment() {
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationData, setVerificationData] = useState<{
    phone: string;
    expiresAt: string;
  } | null>(null);
  const [isVerified, setIsVerified] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentType | null>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    getValues,
  } = useForm<PaymentFormData>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
    },
  });

  // Mutation para enviar código de verificação
  const sendCodeMutation = useMutation({
    mutationFn: async (phone: string) => {
      const response = await verificationAPI.sendCode({
        phone: removePhoneMask(phone),
      });
      // response.data = { data: {...}, timestamp }
      // Extrair os dados reais
      return response.data.data as SendVerificationCodeResponse;
    },
    onSuccess: (data) => {
      const values = getValues();
      setVerificationData({
        phone: values.phone,
        expiresAt: data.expiresAt,
      });
      setShowVerificationModal(true);
      toast.success('Código enviado via WhatsApp!');
    },
    onError: () => {
      toast.error('Erro ao enviar código de verificação. Tente novamente.');
    },
  });

  // Mutation para criar preferência de pagamento
  const createPreferenceMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPaymentType) {
        throw new Error('Selecione um tipo de pagamento');
      }

      const values = getValues();

      // Definir valores baseado no tipo de pagamento
      const amount = selectedPaymentType === PaymentType.SINGLE ? 6000.0 : 7200.0;

      const response = await paymentAPI.createPreference({
        name: values.name,
        email: values.email,
        phone: removePhoneMask(values.phone),
        type: selectedPaymentType,
        amount,
      });
      // response.data = { data: {...}, timestamp }
      return response.data.data as CreatePaymentPreferenceResponse;
    },
    onSuccess: (data) => {
      toast.success('Redirecionando para pagamento...');
      // Armazenar o initPoint no localStorage para possível retry em caso de falha
      localStorage.setItem('mercadopago_init_point', data.initPoint);
      // Redirecionar para o Mercado Pago
      window.location.href = data.initPoint;
    },
    onError: () => {
      toast.error('Erro ao criar preferência de pagamento. Tente novamente.');
      setIsVerified(false); // Permitir tentar novamente
    },
  });

  const onSubmit = (data: PaymentFormData) => {
    // Validar se um tipo de pagamento foi selecionado
    if (!selectedPaymentType) {
      toast.error('Por favor, selecione uma forma de pagamento');
      return;
    }

    // Enviar código de verificação
    sendCodeMutation.mutate(data.phone);
  };

  const handleVerified = () => {
    setIsVerified(true);
    toast.success('Telefone verificado com sucesso!');
    // Criar preferência de pagamento e redirecionar
    createPreferenceMutation.mutate();
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] dark:bg-[#0f0606] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <CreditCard className="h-6 w-6 text-primary" />
            </div>
            <CardTitle>Pagamento</CardTitle>
          </div>
          <CardDescription>Preencha seus dados para prosseguir com o pagamento</CardDescription>
        </CardHeader>

        <CardContent>
          {/* Payment Type Selection */}
          <div className="mb-6 space-y-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Como você deseja pagar?
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSelectedPaymentType(PaymentType.SINGLE)}
                disabled={isVerified}
                className={`
                  relative p-4 border-2 rounded-lg transition-all duration-200
                  ${
                    selectedPaymentType === PaymentType.SINGLE
                      ? 'border-primary bg-primary/5 dark:bg-primary/10'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }
                  ${isVerified ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                <div className="text-left">
                  <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">À Vista</div>
                  <div className="text-2xl font-bold text-primary mb-1">R$ 6.000,00</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    Pagamento único no cartão
                  </div>
                </div>
                {selectedPaymentType === PaymentType.SINGLE && (
                  <div className="absolute top-2 right-2">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                  </div>
                )}
              </button>

              <button
                type="button"
                onClick={() => setSelectedPaymentType(PaymentType.INSTALLMENTS)}
                disabled={isVerified}
                className={`
                  relative p-4 border-2 rounded-lg transition-all duration-200
                  ${
                    selectedPaymentType === PaymentType.INSTALLMENTS
                      ? 'border-primary bg-primary/5 dark:bg-primary/10'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }
                  ${isVerified ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                <div className="text-left">
                  <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    Parcelado
                  </div>
                  <div className="text-2xl font-bold text-primary mb-1">R$ 7.200,00</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    Em até 12x sem juros
                  </div>
                </div>
                {selectedPaymentType === PaymentType.INSTALLMENTS && (
                  <div className="absolute top-2 right-2">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                  </div>
                )}
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <Input
              label="Nome Completo"
              type="text"
              placeholder="João da Silva"
              error={errors.name?.message}
              {...register('name')}
              disabled={isVerified}
            />

            <Input
              label="E-mail"
              type="email"
              placeholder="seu@email.com"
              error={errors.email?.message}
              {...register('email')}
              disabled={isVerified}
            />

            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  label="Número de Whatsapp"
                  error={errors.phone?.message}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={isVerified}
                />
              )}
            />

            {isVerified && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  {createPreferenceMutation.isPending ? (
                    <Loader2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-green-900 dark:text-green-100">
                      {createPreferenceMutation.isPending
                        ? 'Preparando pagamento...'
                        : 'Telefone verificado com sucesso'}
                    </p>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      {createPreferenceMutation.isPending
                        ? 'Aguarde enquanto redirecionamos você'
                        : 'Você pode prosseguir com o pagamento'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="pt-4">
              <Button
                type="submit"
                className="w-full"
                disabled={sendCodeMutation.isPending || isVerified}
              >
                {sendCodeMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enviando código...
                  </>
                ) : isVerified ? (
                  'Prosseguir com Pagamento'
                ) : (
                  'Avançar'
                )}
              </Button>
            </div>

            {!isVerified && (
              <p className="text-xs text-center text-gray-600 dark:text-gray-400">
                Seu WhatsApp será usado para enviar confirmações e informações importantes sobre o
                serviço
              </p>
            )}
          </form>
        </CardContent>
      </Card>

      {verificationData && (
        <VerificationModal
          isOpen={showVerificationModal}
          onClose={() => setShowVerificationModal(false)}
          phone={verificationData.phone}
          expiresAt={verificationData.expiresAt}
          onVerified={handleVerified}
        />
      )}
    </div>
  );
}
